// Catálogo das trilhas oficiais do Agentic League — versão ID-based.
//
// Cada trilha é definida com "course slots" lógicos. Um slot lista um ou mais
// `courseIds` do TalentLMS; o slot fica satisfeito quando QUALQUER UM desses
// IDs estiver presente no conjunto de cursos concluídos do usuário. Isso
// permite:
//   - colapsar variantes EN/PT do mesmo curso lógico (ex.: Lean Basics
//     [EN] e [PT]) em um único slot listando ambos IDs;
//   - aceitar variantes "curso de aprendizagem" + "exame de certificação"
//     como equivalentes (ex.: BCP Simplified curso vs exame);
//   - listar a certificação oficial Anthropic em `overrideCourses` para que
//     ela sozinha curto-circuite a trilha para 100% (preparatórios viram
//     opcionais).
//
// Sanidade dos IDs (não somem do LMS, nomes ainda batem) é checada pelo
// `scripts/smoke-trilhas-resolve.ts`.

export type TrilhaId =
  | "gen-ai-engineering-fast-track"
  | "gen-ai-orchestrator"
  | "gen-ai-soft-skills"
  | "anthropic-developer-foundations"
  | "anthropic-architect-foundations"
  | "anthropic-architect-professional";

export type TrilhaTier = "standard" | "anthropic";
export type TrilhaTrack = "tech" | "gestao";
export type TrilhaVariant = "completa" | "fast-track";

/**
 * Slot lógico de "curso" dentro de uma trilha. O slot é satisfeito se QUALQUER
 * um dos `courseIds` aparece no conjunto certificado do usuário.
 */
export interface TrilhaCourseSlot {
  /** Rótulo de exibição do slot (curado, não vem do LMS). */
  label: string;
  /** IDs do TalentLMS que satisfazem este slot. */
  courseIds: readonly number[];
}

export interface Trilha {
  id: TrilhaId;
  /** Nome de exibição (proper noun — fica em EN no PT-BR também). */
  name: string;
  variant?: TrilhaVariant;
  courseSlots: readonly TrilhaCourseSlot[];
  tier: TrilhaTier;
  /** Pontos a 70%+ (sub-100%). Anthropic tier: 0 (não pontua sem certificação). */
  partialPoints: number;
  /** Pontos a 100% + certificação. Standard: 500; Anthropic: 1000. */
  fullPoints: number;
  tracks: readonly TrilhaTrack[];
  /** True quando a trilha conta para a elegibilidade ao prêmio financeiro
   *  (regulamento, seção 8.2). */
  countsForPrizeEligibility: boolean;
  /**
   * Cursos que, se certificados, sozinhos conferem a trilha completa
   * (progress = 1, full points). Basta UM courseId destes aparecer em
   * `certifiedCourseIds` para disparar o short-circuit.
   *
   * Caso de uso: certificações oficiais (ex.: Anthropic "Claude Certified
   * Architect – Foundations") que substituem a obrigatoriedade dos cursos
   * preparatórios.
   */
  overrideCourses?: readonly TrilhaCourseSlot[];
  /**
   * IDs de cursos de uma versão anterior da trilha (ex.: reemitida para uma
   * fase nova com novos courseIds). Dá 100% de crédito retroativo, mas só se
   * TODOS estes IDs estiverem certificados (all-or-nothing) — progresso
   * parcial nos cursos antigos é descartado e o cálculo passa a considerar
   * apenas os `courseSlots` atuais.
   */
  legacyFullCredit?: readonly number[];
}

/**
 * Definições oficiais das trilhas — fonte da verdade para o que conta em cada
 * uma. IDs vêm do catálogo `/courses` do TalentLMS (CI&T University).
 *
 * Para auditar/atualizar IDs após mudanças no LMS, rode:
 *   npx tsx scripts/smoke-trilhas-resolve.ts
 */
export const TRILHAS: readonly Trilha[] = [
  {
    id: "gen-ai-engineering-fast-track",
    name: "Gen AI Engineering Fast Track",
    variant: "fast-track",
    // Fast Track = subset enxuto de Engineering (sem 1, 3, 6, 7) + suite Anthropic.
    courseSlots: [
      { label: "2. Practical Development with AI Tools", courseIds: [470] },
      { label: "4. Multi-Agent Systems and Collaboration", courseIds: [472] },
      { label: "5. Evaluation and Memory in AI Agents", courseIds: [473] },
      { label: "8. Agentic AI: Architectures and Applications", courseIds: [476] },
      { label: "Agentic Software Development – SDD & BMAD", courseIds: [479] },
      { label: "Claude 101 — Anthropic", courseIds: [498] },
      { label: "Building with the Claude API — Anthropic", courseIds: [483] },
      { label: "Claude Code in Action — Anthropic", courseIds: [484] },
      { label: "Introduction to Agent Skills — Anthropic", courseIds: [482] },
      { label: "Model Context Protocol — Anthropic", courseIds: [480] },
      { label: "Model Context Protocol: Advanced Topics — Anthropic", courseIds: [499] },
    ],
    tier: "standard",
    partialPoints: 250,
    fullPoints: 500,
    tracks: ["tech", "gestao"],
    countsForPrizeEligibility: true,
  },
  {
    id: "gen-ai-orchestrator",
    name: "Gen AI Orchestrator",
    courseSlots: [
      { label: "Being an Orchestrator", courseIds: [486] },
      {
        label: "Agentic AI Fundamentals: Architectures, Frameworks, and Applications",
        courseIds: [489],
      },
      { label: "Preparing for the Future of Work with AI Agents", courseIds: [490] },
      // EN (478) ou PT (512) — variantes do mesmo curso lógico.
      { label: "BCP Simplified", courseIds: [478, 512] },
      { label: "Flow Ops", courseIds: [458] },
      { label: "Gen AI Orchestrator - First Steps", courseIds: [493] },
      // EN (461) ou PT (441) — variantes do mesmo curso lógico.
      { label: "Lean Basics Certification", courseIds: [461, 441] },
      { label: "[Exam] Gen AI Orchestrator", courseIds: [492] },
    ],
    tier: "standard",
    partialPoints: 250,
    fullPoints: 500,
    tracks: ["tech", "gestao"],
    countsForPrizeEligibility: true,
  },
  {
    id: "gen-ai-soft-skills",
    name: "Gen AI Soft Skills",
    // Fase Pocket reemitiu os 5 exames com novos courseIds (527-531). Quem já
    // tinha completado TODOS os 5 antigos (504-508) mantém 100% via
    // legacyFullCredit; progresso parcial nos antigos é descartado.
    courseSlots: [
      { label: "1. Adaptability", courseIds: [527] },
      { label: "2. Assertive Communication", courseIds: [528] },
      { label: "3. Influencing", courseIds: [529] },
      { label: "4. Stakeholder Management & Negotiation", courseIds: [530] },
      { label: "5. Critical Thinking", courseIds: [531] },
    ],
    tier: "standard",
    partialPoints: 250,
    fullPoints: 500,
    tracks: ["tech", "gestao"],
    countsForPrizeEligibility: true,
    legacyFullCredit: [504, 505, 506, 507, 508],
  },
  {
    id: "anthropic-developer-foundations",
    name: "Claude Certified Developer — Foundations",
    // As 3 trilhas Anthropic compartilham os mesmos 7 cursos-prep (AI Fluency
    // + a suite Claude 101/API/Code/Skills/MCP/MCP Advanced); só o exame
    // final (override) muda por trilha.
    courseSlots: [
      { label: "AI Fluency: Framework & Foundations — Anthropic", courseIds: [521] },
      { label: "Claude 101 — Anthropic", courseIds: [498] },
      { label: "Building with the Claude API — Anthropic", courseIds: [483] },
      { label: "Claude Code in Action — Anthropic", courseIds: [484] },
      { label: "Introduction to Agent Skills — Anthropic", courseIds: [482] },
      { label: "Model Context Protocol — Anthropic", courseIds: [480] },
      { label: "Model Context Protocol: Advanced Topics — Anthropic", courseIds: [499] },
      {
        label: "[Final Exam Certification] Claude Certified Developer – Foundations",
        courseIds: [519],
      },
    ],
    tier: "anthropic",
    partialPoints: 0,
    fullPoints: 1000,
    tracks: ["tech", "gestao"],
    countsForPrizeEligibility: true,
    // Ter passado o exame oficial sozinho já certifica a trilha inteira —
    // os preparatórios viram opcionais.
    overrideCourses: [
      {
        label: "[Final Exam Certification] Claude Certified Developer – Foundations",
        courseIds: [519],
      },
    ],
  },
  {
    id: "anthropic-architect-foundations",
    name: "Claude Certified Architect — Foundations",
    courseSlots: [
      { label: "AI Fluency: Framework & Foundations — Anthropic", courseIds: [521] },
      { label: "Claude 101 — Anthropic", courseIds: [498] },
      { label: "Building with the Claude API — Anthropic", courseIds: [483] },
      { label: "Claude Code in Action — Anthropic", courseIds: [484] },
      { label: "Introduction to Agent Skills — Anthropic", courseIds: [482] },
      { label: "Model Context Protocol — Anthropic", courseIds: [480] },
      { label: "Model Context Protocol: Advanced Topics — Anthropic", courseIds: [499] },
      { label: "Claude Certified Architect — Foundations", courseIds: [500] },
    ],
    tier: "anthropic",
    partialPoints: 0,
    fullPoints: 1000,
    tracks: ["tech", "gestao"],
    countsForPrizeEligibility: true,
    // Ter passado o exame oficial sozinho já certifica a trilha inteira —
    // os preparatórios viram opcionais.
    overrideCourses: [
      { label: "[Certification] Claude Certified Architect – Foundations", courseIds: [500] },
    ],
  },
  {
    id: "anthropic-architect-professional",
    name: "Claude Certified Architect — Professional",
    courseSlots: [
      { label: "AI Fluency: Framework & Foundations — Anthropic", courseIds: [521] },
      { label: "Claude 101 — Anthropic", courseIds: [498] },
      { label: "Building with the Claude API — Anthropic", courseIds: [483] },
      { label: "Claude Code in Action — Anthropic", courseIds: [484] },
      { label: "Introduction to Agent Skills — Anthropic", courseIds: [482] },
      { label: "Model Context Protocol — Anthropic", courseIds: [480] },
      { label: "Model Context Protocol: Advanced Topics — Anthropic", courseIds: [499] },
      {
        label: "[Final Exam Certification] Claude Certified Architect – Professional",
        courseIds: [520],
      },
    ],
    tier: "anthropic",
    partialPoints: 0,
    fullPoints: 1000,
    tracks: ["tech", "gestao"],
    countsForPrizeEligibility: true,
    // Ter passado o exame oficial sozinho já certifica a trilha inteira —
    // os preparatórios viram opcionais.
    overrideCourses: [
      {
        label: "[Final Exam Certification] Claude Certified Architect – Professional",
        courseIds: [520],
      },
    ],
  },
] as const;

export const TRILHA_IDS = TRILHAS.map((t) => t.id) as TrilhaId[];

export const TRILHA_BY_ID: Record<TrilhaId, Trilha> = Object.fromEntries(
  TRILHAS.map((t) => [t.id, t]),
) as Record<TrilhaId, Trilha>;

export function trilhaById(id: TrilhaId): Trilha | undefined {
  return TRILHAS.find((t) => t.id === id);
}

/**
 * Conjunto deduplicado de todos os courseIds referenciados — união sobre
 * slots normais, `overrideCourses` e `legacyFullCredit` em todas as trilhas.
 * Usado para iterar os endpoints `/courses/id:{N}` do TalentLMS.
 */
export function allCourseIds(trilhas: readonly Trilha[]): number[] {
  const seen = new Set<number>();
  for (const t of trilhas) {
    for (const slot of t.courseSlots) {
      for (const id of slot.courseIds) seen.add(id);
    }
    if (t.overrideCourses) {
      for (const slot of t.overrideCourses) {
        for (const id of slot.courseIds) seen.add(id);
      }
    }
    if (t.legacyFullCredit) {
      for (const id of t.legacyFullCredit) seen.add(id);
    }
  }
  return Array.from(seen);
}

/**
 * Progresso em uma trilha como #slots-satisfeitos / #slots, com short-circuits:
 * - `legacyFullCredit`: se TODOS os IDs legados estiverem certificados,
 *   progress = 1 (crédito retroativo). Progresso parcial nos legados é
 *   ignorado — cai para o cálculo normal pelos `courseSlots` atuais.
 * - `overrideCourses`: se QUALQUER override course estiver certificado,
 *   progress = 1 — os slots normais são ignorados.
 *
 * Um slot está satisfeito se QUALQUER um de seus `courseIds` aparece em
 * `certifiedCourseIds`.
 */
export function trilhaProgress(
  trilha: Trilha,
  certifiedCourseIds: ReadonlyArray<number> | ReadonlySet<number>,
): number {
  const certified =
    certifiedCourseIds instanceof Set ? certifiedCourseIds : new Set(certifiedCourseIds);

  if (trilha.legacyFullCredit?.every((id) => certified.has(id))) {
    return 1;
  }

  if (trilha.overrideCourses?.some((s) => s.courseIds.some((id) => certified.has(id)))) {
    return 1;
  }

  if (trilha.courseSlots.length === 0) return 0;
  let hits = 0;
  for (const slot of trilha.courseSlots) {
    if (slot.courseIds.some((id) => certified.has(id))) hits++;
  }
  return hits / trilha.courseSlots.length;
}

/** Cria um Record<TrilhaId, T> aplicando `fn` a cada trilha. */
export function mapTrilhas<T>(fn: (trilha: Trilha) => T): Record<TrilhaId, T> {
  const out = {} as Record<TrilhaId, T>;
  for (const trilha of TRILHAS) out[trilha.id] = fn(trilha);
  return out;
}

/**
 * Pontos por trilha, conforme regulamento seção 7.1:
 * - standard: 0 a <70%, 250 a [70%, 100%), 500 a 100% (com certificação).
 * - anthropic: 0 até <100%, 1000 a 100% (com certificação).
 *
 * `progressFraction` em [0..1].
 */
export function trilhaPoints(trilha: Trilha, progressFraction: number): number {
  if (progressFraction >= 1) return trilha.fullPoints;
  if (trilha.tier === "standard" && progressFraction >= 0.7) return trilha.partialPoints;
  return 0;
}

/** Alias semântico para o regulamento — mesma semântica que `trilhaPoints`. */
export const computeTrilhaPoints = trilhaPoints;
